// Face check — the layer ABOVE Clock In.
//
// Every call goes to hrms (:8000), never to face-service directly. face-service
// will verify any user_id it is handed and has no notion of who is allowed to
// ask; hrms holds the JWT and decides that. Pointing the browser at :8003 would
// hand anyone an unauthenticated face-verification endpoint.
import api from '../api/api';
import { clientAttendanceMode } from './attendance';

export interface FaceStatus {
  /** Is the face layer switched on for this deployment at all? */
  enabled: boolean;
  /** Has THIS user been granted the "Face Check-In" panel in Access
   *  Management? Off for everyone until an admin turns it on. Kept separate
   *  from `enabled` so "no camera appeared" is diagnosable: a deployment-wide
   *  flag and one person's missing grant look identical to the user but need
   *  completely different fixes. Absent on an older hrms — treated as false. */
  allowed?: boolean;
  /** Is face-service actually reachable and ready? When false the UI falls
   *  back to the ordinary Clock In — a side-car being down must not stop
   *  somebody being marked present. */
  available: boolean;
  enrolled: boolean;
  template_count: number;
  consent: boolean;
  message?: string | null;
}

export interface FrameCheck {
  ok: boolean;
  outcome: string;
  message: string;
  scores: { quality?: number | null; liveness?: number | null; face_pixels?: number | null };
}

export interface EnrollResult {
  ok: boolean;
  accepted_frames: number;
  rejected: string[];
  message: string;
}

/** What a failed verification tells the caller. `outcome` is what to DO:
 *  not_enrolled opens the wizard, cooldown means wait, the rest mean retry. */
export interface FaceFailure {
  message: string;
  outcome?: string;
  scores?: Record<string, number | null>;
}

export const getFaceStatus = async (): Promise<FaceStatus> =>
  (await api.get<FaceStatus>('/attendance/face/status')).data;

export const grantFaceConsent = async (): Promise<void> => {
  await api.post('/attendance/face/consent');
};

/** Withdraw consent AND erase the stored face. Self-service on purpose: a
 *  biometric you cannot delete yourself is one you never really consented to. */
export const revokeFaceConsent = async (): Promise<{ templates_deleted: number }> =>
  (await api.delete('/attendance/face/consent')).data;

/** Live framing feedback for the wizard. Detection + quality only — spoof
 *  detection runs on the frames actually committed, not on every preview tick. */
export const checkFrame = async (imageB64: string, strict = false): Promise<FrameCheck> =>
  (await api.post<FrameCheck>('/attendance/face/check-frame', {
    image_b64: imageB64,
    strict,
  })).data;

export const enrollFace = async (imagesB64: string[]): Promise<EnrollResult> =>
  (await api.post<EnrollResult>('/attendance/face/enroll', { images_b64: imagesB64 })).data;

/** Verify and clock in, in one call. Returns whatever the ordinary clock-in
 *  returns, plus the face scores. Throws on rejection — read `outcome` from
 *  the error body to decide what to show. */
export const faceClockIn = async (
  imageB64: string,
  opts: {
    workfromhome?: boolean;
    latitude?: string | null;
    longitude?: string | null;
    accuracy?: number | null;
  } = {},
): Promise<any> =>
  (await api.post('/attendance/face/clock-in', {
    image_b64: imageB64,
    workfromhome: opts.workfromhome ?? false,
    latitude: opts.latitude ?? null,
    longitude: opts.longitude ?? null,
    accuracy: opts.accuracy ?? null,
    // A face punch still comes from one of the four clients — the camera only
    // authorised it. Same value the ordinary Clock In sends.
    attendance_mode: clientAttendanceMode(),
  })).data;

/** Pull the useful part out of an axios error from the two endpoints above.
 *  hrms sends a 403 with `{message, outcome, scores}` for a genuine rejection
 *  and a 503 with a plain string when the service is unavailable — the two
 *  mean completely different things to the user. */
export const readFaceFailure = (err: any): FaceFailure => {
  const detail = err?.response?.data?.detail;
  if (detail && typeof detail === 'object') {
    return {
      message: detail.message || 'Face check failed.',
      outcome: detail.outcome,
      scores: detail.scores,
    };
  }
  return {
    message: typeof detail === 'string' ? detail : (err?.message || 'Face check failed.'),
    outcome: err?.response?.status === 503 ? 'unavailable' : undefined,
  };
};
